// Servicio de geolocalización con fallbacks progresivos
import { safariGeolocationService, SafariGeolocationService } from './safariGeolocationService';

export class GeolocationService {
    constructor() {
        this.options = {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000 // 5 minutos de cache
        };
    }

    // 🆕 Detectar Safari y usar servicio específico
    _isSafari() {
        return SafariGeolocationService.isSafari();
    }

    // Nivel 1: Ubicación exacta (GPS) - Mejorado para Safari y navegadores embebidos
    async getExactLocation() {
        // Primero verificar si es un navegador embebido (Instagram, Facebook, etc.)
        const inAppBrowser = SafariGeolocationService.isInAppBrowser();
        if (inAppBrowser) {
            throw new Error(`Navegador de ${inAppBrowser.name} detectado: La geolocalización no está disponible en navegadores embebidos. Por favor, abre la aplicación en tu navegador normal.`);
        }

        // Si es Safari, usar el servicio específico
        if (this._isSafari()) {
            try {
                const safariResult = await safariGeolocationService.getLocationSafariCompatible();
                return {
                    ...safariResult,
                    type: 'exact-safari'
                };
            } catch (error) {
                console.warn('Safari service falló, intentando método estándar:', error.message);
                // Fallback al método estándar
            }
        }

        // Método estándar para otros navegadores
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Geolocalización no soportada'));
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                        type: 'exact'
                    });
                },
                (error) => {
                    reject(this._handleGeolocationError(error));
                },
                this.options
            );
        });
    }

    // Nivel 2: Ubicación aproximada (solo ciudad/zona) - Mejorado para Safari y navegadores embebidos
    async getApproximateLocation() {
        // Primero verificar si es un navegador embebido (Instagram, Facebook, etc.)
        const inAppBrowser = SafariGeolocationService.isInAppBrowser();
        if (inAppBrowser) {
            throw new Error(`Navegador de ${inAppBrowser.name} detectado: La geolocalización no está disponible en navegadores embebidos. Por favor, abre la aplicación en tu navegador normal.`);
        }

        // Si es Safari, usar configuración específica
        if (this._isSafari()) {
            try {
                const safariResult = await safariGeolocationService.getLocationSafariCompatible();
                return {
                    ...safariResult,
                    type: 'approximate-safari'
                };
            } catch (error) {
                console.warn('Safari approximate falló:', error.message);
                throw error;
            }
        }

        // Método estándar
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Geolocalización no soportada'));
                return;
            }

            const lowAccuracyOptions = {
                enableHighAccuracy: false,
                timeout: 15000,
                maximumAge: 600000 // 10 minutos de cache para ubicación aproximada
            };

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                        type: 'approximate'
                    });
                },
                (error) => {
                    reject(this._handleGeolocationError(error));
                },
                lowAccuracyOptions
            );
        });
    }

    // Convertir coordenadas a nombre de ubicación usando múltiples proveedores - Optimizado para Safari
    async reverseGeocode(latitude, longitude) {
        // Si es Safari, usar el método optimizado
        if (this._isSafari()) {
            try {
                const safariResult = await safariGeolocationService.reverseGeocodeSafariCompatible(latitude, longitude);
                if (safariResult) {
                    return safariResult;
                }
            } catch (error) {
                console.warn('Safari geocoding falló, usando método estándar:', error.message);
            }
        }

        const providers = [
            {
                name: 'locationiq',
                url: `https://us1.locationiq.com/v1/reverse.php?key=${process.env.REACT_APP_LOCATIONIQ_API_KEY}&lat=${latitude}&lon=${longitude}&format=json&accept-language=es`,
                free: false, // 5000 requests gratis por día
                requests: 5000
            },
            {
                name: 'opencage',
                url: `https://api.opencagedata.com/geocode/v1/json?q=${latitude}+${longitude}&key=${process.env.REACT_APP_OPENCAGE_API_KEY}&language=es&no_annotations=1`,
                free: false, // 2500 requests gratis por día
                requests: 2500
            },
            {
                name: 'positionstack',
                url: `http://api.positionstack.com/v1/reverse?access_key=${process.env.REACT_APP_POSITIONSTACK_API_KEY}&query=${latitude},${longitude}&limit=1`,
                free: false, // 25000 requests gratis por mes
                requests: 25000
            },
            {
                name: 'nominatim_fallback',
                url: `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1&accept-language=es`,
                free: true, // Solo como último recurso
                requests: 1
            }
        ];

        // Usar servicios con API keys disponibles tanto en desarrollo como en producción
        // Priorizar LocationIQ si está disponible
        const activeProviders = [];

        // Primero intentar con servicios pagos (LocationIQ, OpenCage, PositionStack)
        if (this._hasApiKey('locationiq')) {
            activeProviders.push(providers[0]); // LocationIQ
        }
        if (this._hasApiKey('opencage')) {
            activeProviders.push(providers[1]); // OpenCage
        }
        if (this._hasApiKey('positionstack')) {
            activeProviders.push(providers[2]); // PositionStack
        }

        // Si no hay servicios pagos disponibles, usar Nominatim como fallback
        if (activeProviders.length === 0) {
            activeProviders.push(providers[3]); // Nominatim fallback
        }

        for (const provider of activeProviders) {
            try {
                const result = await this._tryProvider(provider, latitude, longitude);
                if (result) {
                    return result;
                }
            } catch (error) {
                console.warn(`❌ Falló ${provider.name}:`, error.message);
            }
        }

        // Si todos fallan, usar coordenadas como fallback
        return {
            city: '',
            state: '',
            country: '',
            displayName: `Lat: ${latitude.toFixed(4)}, Lon: ${longitude.toFixed(4)}`,
            fullAddress: {},
            isCoordinatesOnly: true
        };
    }

    async _tryProvider(provider, latitude, longitude) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);

        try {
            let url = provider.url;

            // Reemplazar API keys desde variables de entorno
            if (provider.name === 'locationiq') {
                url = url.replace('YOUR_API_KEY', process.env.REACT_APP_LOCATIONIQ_API_KEY || '');
            } else if (provider.name === 'opencage') {
                url = url.replace('YOUR_API_KEY', process.env.REACT_APP_OPENCAGE_API_KEY || '');
            } else if (provider.name === 'positionstack') {
                url = url.replace('YOUR_API_KEY', process.env.REACT_APP_POSITIONSTACK_API_KEY || '');
            }

            const response = await fetch(url, {
                signal: controller.signal,
                headers: {
                    'User-Agent': 'ContactaMe App',
                    'Accept': 'application/json'
                }
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            return this._parseProviderResponse(provider.name, data);

        } catch (error) {
            clearTimeout(timeoutId);
            throw error;
        }
    }

    _parseProviderResponse(providerName, data) {
        switch (providerName) {
            case 'locationiq':
                // LocationIQ usa una estructura con "address" object
                const address = data.address || {};
                const locationInfo = this._formatLocationIQResponse(data, address);

                return {
                    city: locationInfo.primaryLocation,
                    state: locationInfo.state,
                    country: address.country || '',
                    displayName: locationInfo.displayName,
                    // Información completa para SmartLocation
                    smartLocation: {
                        approximate: false, // GPS es siempre exacto
                        coordinates: {
                            lat: parseFloat(data.lat),
                            lng: parseFloat(data.lon)
                        },
                        displayName: locationInfo.displayName,
                        lastUpdated: new Date().toISOString(),
                        originalLocation: data.display_name,
                        serviceLocationId: data.place_id,
                        source: "GPS"
                    },
                    fullAddress: {
                        place_id: data.place_id,
                        osm_type: data.osm_type,
                        osm_id: data.osm_id,
                        lat: data.lat,
                        lon: data.lon,
                        display_name: data.display_name,
                        // Datos del address object
                        residential: address.residential || '',
                        village: address.village || '',
                        town: address.town || '',
                        city: address.city || '',
                        municipality: address.municipality || '',
                        county: address.county || '',
                        state_district: address.state_district || '',
                        state: address.state || '',
                        postcode: address.postcode || '',
                        country: address.country || '',
                        country_code: address.country_code || '',
                        // Otros campos comunes
                        house_number: address.house_number || '',
                        road: address.road || '',
                        suburb: address.suburb || '',
                        quarter: address.quarter || ''
                    }
                };

            case 'opencage':
                const components = data.results?.[0]?.components || {};
                return {
                    city: components.city || components.town || components.village || '',
                    state: components.state || components.province || '',
                    country: components.country || '',
                    displayName: data.results?.[0]?.formatted || '',
                    fullAddress: components
                };

            case 'positionstack':
                const location = data.data?.[0] || {};
                return {
                    city: location.locality || location.neighbourhood || '',
                    state: location.region || '',
                    country: location.country || '',
                    displayName: location.label || '',
                    fullAddress: location
                };

            case 'nominatim_fallback':
                return {
                    city: data.address?.city || data.address?.town || data.address?.village || '',
                    state: data.address?.state || data.address?.province || '',
                    country: data.address?.country || '',
                    displayName: data.display_name || '',
                    fullAddress: data.address || {}
                };

            default:
                return null;
        }
    }

    // Optimizada para la estructura real de LocationIQ
    _formatLocationIQResponse(data, address) {
        // PRIORIDAD: Village primero (como solicitaste)
        let primaryLocation = '';
        let state = address.state || address.state_district || '';

        // Priorización específica para LocationIQ
        if (address.village && address.village.toLowerCase() !== 'residential') {
            primaryLocation = address.village;
        } else if (address.city && address.city.toLowerCase() !== 'residential') {
            primaryLocation = address.city;
        } else if (address.town && address.town.toLowerCase() !== 'residential') {
            primaryLocation = address.town;
        } else if (address.municipality && address.municipality.toLowerCase() !== 'residential') {
            primaryLocation = address.municipality;
        } else if (address.suburb && address.suburb.toLowerCase() !== 'residential') {
            primaryLocation = address.suburb;
        } else if (address.quarter && address.quarter.toLowerCase() !== 'residential') {
            primaryLocation = address.quarter;
        } else if (address.county) {
            primaryLocation = address.county;
        } else if (address.state_district) {
            primaryLocation = address.state_district;
        }

        // Para display: SOLO village o fallback inteligente
        let displayName = '';
        if (address.village && address.village.toLowerCase() !== 'residential') {
            displayName = address.village; // SOLO village como solicitaste
        } else if (primaryLocation) {
            displayName = primaryLocation;
        } else if (state) {
            displayName = state;
        } else if (address.country) {
            displayName = address.country;
        } else {
            displayName = data.display_name || 'Ubicación detectada';
        }

        return {
            primaryLocation,
            state,
            displayName
        };
    }

    _hasApiKey(providerName) {
        switch (providerName) {
            case 'locationiq':
                return !!process.env.REACT_APP_LOCATIONIQ_API_KEY;
            case 'opencage':
                return !!process.env.REACT_APP_OPENCAGE_API_KEY;
            case 'positionstack':
                return !!process.env.REACT_APP_POSITIONSTACK_API_KEY;
            default:
                return false;
        }
    }

    // Buscar la ubicación más cercana en tu lista de ubicaciones disponibles
    findClosestLocation(userCoords, availableLocations) {
        if (!availableLocations || availableLocations.length === 0) {
            return null;
        }

        let closestLocation = null;
        let minDistance = Infinity;

        availableLocations.forEach(location => {
            // Verificar si la ubicación tiene coordenadas en centerCoordinates (estructura Firestore)
            const locationCoords = this._getLocationCoordinates(location);

            if (locationCoords) {
                const distance = this._calculateDistance(
                    userCoords.latitude,
                    userCoords.longitude,
                    locationCoords.lat,
                    locationCoords.lng
                );


                // Verificar si el usuario está dentro del radio de la ubicación
                const radiusKm = (location.radiusMeters || 0) / 1000; // Convertir metros a km

                if (distance <= radiusKm && distance < minDistance) {
                    minDistance = distance;
                    closestLocation = location;
                }
            }
        });



        return closestLocation;
    }

    // Método auxiliar para obtener coordenadas de una ubicación
    _getLocationCoordinates(location) {
        // Estructura de Firestore: centerCoordinates.lat/lng
        if (location.centerCoordinates && location.centerCoordinates.lat && location.centerCoordinates.lng) {
            return {
                lat: location.centerCoordinates.lat,
                lng: location.centerCoordinates.lng
            };
        }

        // Fallback: estructura legacy latitude/longitude
        if (location.latitude && location.longitude) {
            return {
                lat: location.latitude,
                lng: location.longitude
            };
        }

        return null;
    }

    // Buscar ubicación por nombre de ciudad
    findLocationByCity(cityName, availableLocations) {
        if (!cityName || !availableLocations) return null;

        const normalizedCity = cityName.toLowerCase().trim();

        return availableLocations.find(location =>
            location.name.toLowerCase().includes(normalizedCity) ||
            normalizedCity.includes(location.name.toLowerCase())
        );
    }

    // Calcular distancia entre dos puntos (fórmula de Haversine)
    _calculateDistance(lat1, lon1, lat2, lon2) {
        const R = 6371; // Radio de la Tierra en km
        const dLat = this._degToRad(lat2 - lat1);
        const dLon = this._degToRad(lon2 - lon1);
        const a =
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(this._degToRad(lat1)) * Math.cos(this._degToRad(lat2)) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
    }

    _degToRad(deg) {
        return deg * (Math.PI/180);
    }

    _handleGeolocationError(error) {
        switch(error.code) {
            case error.PERMISSION_DENIED:
                return new Error('El usuario denegó el acceso a la ubicación');
            case error.POSITION_UNAVAILABLE:
                return new Error('La información de ubicación no está disponible');
            case error.TIMEOUT:
                return new Error('La solicitud de ubicación ha caducado');
            default:
                return new Error(`Error desconocido de geolocalización: ${error.message}`);
        }
    }
}

export const geolocationService = new GeolocationService();
